import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { HubIndexRedirect, LegacyRouteRedirect } from "./components/hub/HubPages";
import { LegacyPurchasingRedirect } from "./pages/purchasing/workspace/LegacyPurchasingRedirect";
import { useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { useShownRoles } from "./auth/useShownRoles";
import { hubsFor, HUBS, type NavItem } from "./components/hub/hubs";
import { PageSkeleton } from "./components/Skeleton";
import { lazyPage, preloadOf } from "./lib/lazyPage";
import { registerPreload } from "./lib/preload";
import { ScrollMemory } from "./lib/ScrollMemory";
import { useRelease150 } from "./lib/release150";
import { StartLanding } from "./components/home/StartLanding";
import { Release150Route } from "./components/home/Release150Route";
import { AdminOnly, OperationsPrincipals } from "./auth/roles";
import { AssignmentsPage, EfficiencyPage, LoadBoardPage, MyAssignedWorkPage, SkillsPage, TimeOffPage, WeeklyReportPage, peopleRoutes } from "./pages/areas/peopleArea";
import { planningRoutes } from "./pages/areas/planningArea";
import { floorRoutes } from "./pages/areas/floorArea";
import { analyticsRoutes } from "./pages/areas/analyticsArea";
import { customerPresentRoute, customerRoutes } from "./pages/areas/customersArea";
import { peopleInsightsRoutes } from "./pages/areas/peopleInsightsArea";
import { teamsRoutes } from "./pages/areas/teamsArea";
import { customersHubRoutes } from "./pages/areas/customersHubArea";
import { parityRoutes } from "./pages/areas/parityArea";
import { logisticsRoutes } from "./pages/areas/logisticsArea";
import {
  AlertsPage,
  AssemblyTreesPage,
  LineOfBalancePage,
  OutOfSequenceBuyOffPage,
  MasterSchedulePage,
  WipPage,
  WorkOrderDetailPage,
  WorkOrdersPage,
  ReadyForOperationsPage,
  operationsRoutes
} from "./pages/areas/operationsArea";
import { CapacityPage, DispatchPage, HotlistPage, MaterialPage, OspBoardPage, ParkedWorkOrdersPage, SqdcPage, WorkingNowPage, shopFloorRoutes } from "./pages/areas/shopFloorArea";
import { MorningBriefPage, PurchasingPage, ShippingPage, supplyRoutes } from "./pages/areas/supplyArea";
import { NotAuthorizedPage, NotFoundPage, SignInPage, WallDisplayPage } from "./pages/SystemPages";
import { VerifyIdentityPage } from "./pages/access/VerifyIdentityPage";

// One file per area, each owned by one build agent (release 0.2.0 / 0.3.0). App.tsx only wires them in.
import type { AreaRoute } from "./pages/areas/areaRoute";
import { AccessReviewPage, RequestAccessPage, UsersRolesPage, accessRoutes } from "./pages/areas/accessArea";
import {
  AdminNotificationsPage,
  AdoptionPage,
  CommunicationsPage,
  DataHealthPage,
  DataSyncPage,
  FeedbackPage,
  RoiPage,
  WallDisplaysPage,
  platformRoutes
} from "./pages/areas/platformArea";
import {
  AiActivityPage,
  AskViperPage,
  ClearancePage,
  KnowledgeIndexPage,
  RebalancePage,
  ScenarioLabPage,
  viperRoutes
} from "./pages/areas/viperArea";
import { KnowledgeBasePage, ProceduresPage, knowledgeRoutes } from "./pages/areas/knowledgeArea";
import { ApprovalsPage, As9100Page, CmmcPage, FormsRegisterPage, IncidentsPage, RecordsRetentionPage, SspPage, ValidationPage, qualityRoutes } from "./pages/areas/qualityArea";
import { MyProfilePage, MyQuestionsPage, MyTrainingPage, TrainingAdminPage, TrainingCertificatesReviewPage, TrainingGapsPage, TrainingMatrixPage, trainingRoutes } from "./pages/areas/trainingArea";
import { AboutReleaseNotesPage, AdminReleasesPage, AiEthicsPage, AiPoliciesPage, ArchitecturePage, aboutRoutes } from "./pages/areas/aboutArea";
import { MyFeedbackPage, PilotGuidePage, PilotHubPage, pilotRoutes } from "./pages/areas/pilotArea";

// Release 1.5.0 (SE D-0189): every screen is its own chunk, loaded on first visit or when its link is hovered/focused.
const OperationsHubPage = lazyPage(() => import("./pages/operations/OperationsHubPage"), "OperationsHubPage");
const CustodyPage = lazyPage(() => import("./pages/CustodyPage"), "CustodyPage");
const MyNotificationsPage = lazyPage(() => import("./pages/MyNotificationsPage"), "MyNotificationsPage");
const GovernedSettingsPage = lazyPage(() => import("./pages/admin/GovernedSettingsPage"), "GovernedSettingsPage");
const AuditPage = lazyPage(() => import("./pages/admin/AuditPage"), "AuditPage");
const CarrierAdminPage = lazyPage(() => import("./pages/admin/logistics/CarrierAdminPage"), "CarrierAdminPage");
// Release 1.5.0 Home (role-home-1-5b, O-R64–O-R66): the start page and the Admin's role start pages.
const HomePage = lazyPage(() => import("./pages/home/HomePage"), "HomePage");
const RoleStartPagesPage = lazyPage(() => import("./pages/home/RoleStartPagesPage"), "RoleStartPagesPage");
// Release 1.9.0 Logistics hub (D-O152).
const ComingGoingPage = lazyPage(() => import("./pages/logistics/ComingGoingPage"), "ComingGoingPage");
const LogisticsInboxPage = lazyPage(() => import("./pages/logistics/LogisticsInboxPage"), "LogisticsInboxPage");
// W2/W4 clickable prototype (synthetic data, no API): development server or VITE_PROTOTYPES=true only (D-O159).
const LogisticsPrototypePage = lazyPage(() => import("./pages/prototype/LogisticsPrototypePage"), "LogisticsPrototypePage");
const PROTOTYPES_ON = import.meta.env.DEV || import.meta.env.VITE_PROTOTYPES === "true";

/** Detail and editor pages each area adds beyond its menu tabs. */
const AREA_ROUTES: AreaRoute[] = [...accessRoutes, ...platformRoutes, ...viperRoutes, ...knowledgeRoutes, ...aboutRoutes, ...qualityRoutes, ...trainingRoutes, ...operationsRoutes, ...shopFloorRoutes, ...supplyRoutes, ...peopleRoutes, ...planningRoutes, ...floorRoutes, ...analyticsRoutes, ...customerRoutes, ...peopleInsightsRoutes, ...teamsRoutes, ...customersHubRoutes, ...parityRoutes, ...logisticsRoutes, ...pilotRoutes];
for (const r of [...AREA_ROUTES, customerPresentRoute]) {
  const preload = preloadOf(r.element.type);
  if (preload) registerPreload(r.path, preload);
}
registerPreload("/work-orders", WorkOrderDetailPage.preload);
registerPreload("/home", HomePage.preload);

// Every screen has its own URL and its own route guard. The guard's `allow` list is the same list the hub registry
// uses for the menu (components/hub/hubs.ts), so the menu and the routes can never disagree — and the API enforces
// the policy regardless.

/** The page component for each registered screen, by nav item id. Every id in HUBS must appear here. */
const PAGES: Record<string, () => JSX.Element> = {
  "my-work": MyAssignedWorkPage,
  "my-questions": MyQuestionsPage,
  "my-training": MyTrainingPage,
  "my-notifications": MyNotificationsPage,
  "my-profile": () => <MyProfilePage />,
  "my-feedback": MyFeedbackPage,
  training: () => <MyTrainingPage />,
  "training-matrix": TrainingMatrixPage,
  "training-gaps": TrainingGapsPage,
  "training-certificates": TrainingCertificatesReviewPage,
  "training-admin": TrainingAdminPage,

  "operations-hub": OperationsHubPage,
  "work-orders": WorkOrdersPage,
  "ready-for-operations": ReadyForOperationsPage,
  schedule: MasterSchedulePage,
  trees: AssemblyTreesPage,
  lob: LineOfBalancePage,
  "out-of-sequence": OutOfSequenceBuyOffPage,
  wip: WipPage,
  alerts: AlertsPage,
  "weekly-report": WeeklyReportPage,

  dispatch: DispatchPage,
  capacity: CapacityPage,
  material: MaterialPage,
  hotlist: HotlistPage,
  sqdc: SqdcPage,
  "working-now": WorkingNowPage,
  "osp-board": OspBoardPage,
  parked: ParkedWorkOrdersPage,
  shipping: ShippingPage,
  purchasing: PurchasingPage,
  "logistics-coming-going": ComingGoingPage,
  "logistics-inbox": LogisticsInboxPage,

  "load-board": LoadBoardPage,
  assignments: AssignmentsPage,
  skills: SkillsPage,
  efficiency: EfficiencyPage,
  "time-off": TimeOffPage,

  "knowledge-base": KnowledgeBasePage,
  procedures: ProceduresPage,
  incidents: IncidentsPage,

  as9100: As9100Page,
  cmmc: () => <CmmcPage />,
  custody: CustodyPage,
  approvals: ApprovalsPage,
  ssp: SspPage,
  forms: FormsRegisterPage,
  records: RecordsRetentionPage,
  validation: ValidationPage,

  "viper-ask": AskViperPage,
  "viper-brief": MorningBriefPage,
  "viper-scenarios": ScenarioLabPage,
  "viper-rebalance": RebalancePage,

  "users-roles": UsersRolesPage,
  "access-review": AccessReviewPage,
  governed: GovernedSettingsPage,
  "data-sync": DataSyncPage,
  "data-health": DataHealthPage,
  audit: AuditPage,
  "carrier-admin": CarrierAdminPage,
  "ai-activity": AiActivityPage,
  communications: CommunicationsPage,
  "admin-notifications": AdminNotificationsPage,
  clearance: ClearancePage,
  "knowledge-index": KnowledgeIndexPage,
  "wall-displays": WallDisplaysPage,
  feedback: FeedbackPage,
  "pilot-hub": PilotHubPage,
  adoption: AdoptionPage,
  roi: RoiPage,
  "admin-releases": AdminReleasesPage,

  "release-notes": AboutReleaseNotesPage,
  architecture: ArchitecturePage,
  "ai-ethics": AiEthicsPage,
  "ai-policies": AiPoliciesPage,
  "pilot-guide": PilotGuidePage
};

/** Hovering or focusing a link to a screen starts loading its chunk (SE lib\preload.ts). */
for (const item of HUBS.flatMap((hub) => hub.items)) {
  const preload = preloadOf(PAGES[item.id]);
  if (preload) registerPreload(item.to, preload);
}

/** Every registered screen, as a guarded route. */
function screenRoute(item: NavItem) {
  const Page = PAGES[item.id];
  if (!Page) return null;
  // `/operations` also owns `/work-orders/:key`, which has its own route below.
  return (
    <Route
      key={item.id}
      path={item.to}
      element={
        <ProtectedRoute allow={item.allow}>
          <Page />
        </ProtectedRoute>
      }
    />
  );
}

/** Where a person lands: the first tab of the first hub they can use. */
function StartPage() {
  const roles = useShownRoles();
  const { loading, principal } = useAuth();
  const location = useLocation();
  const release150 = useRelease150();
  if (loading) return <PageSkeleton />;
  if (!principal) return <Navigate to="/sign-in" replace state={{ returnTo: `${location.pathname}${location.search}` }} />;
  const first = hubsFor(roles)[0];
  // Release 1.5.0 (O-R64): the person's start page, decided by the server; the line below when the switch is off.
  if (release150) return <StartLanding fallback={first?.to ?? null} />;
  if (!first) return <Navigate to="/request-access" replace />;
  return <Navigate to={first.to} replace />;
}

export default function App() {
  return (
    <>
    {/* Release 1.5.0 (switch on): Back/Forward return to the same scroll position; links open at the top. */}
    <ScrollMemory />
    <Routes>
      {/* Outside the application frame: sign-in and the kiosk board. */}
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/verify" element={<VerifyIdentityPage />} />
      <Route path="/display" element={<WallDisplayPage />} />
      {/* Shown to customers in a briefing: no internal frame, ticker or VIPER (D-O73). */}
      <Route
        path={customerPresentRoute.path}
        element={<ProtectedRoute allow={customerPresentRoute.allow}>{customerPresentRoute.element}</ProtectedRoute>}
      />

      <Route element={<AppShell />}>
        <Route path="/" element={<StartPage />} />
        {/* Release 1.5.0 Home; each page shows "not found" while the switch is off, as before. */}
        <Route path="/home" element={<Release150Route off={<NotFoundPage />}><ProtectedRoute allow={OperationsPrincipals}><HomePage /></ProtectedRoute></Release150Route>} />
        <Route path="/admin/start-pages" element={<Release150Route off={<NotFoundPage />}><ProtectedRoute allow={AdminOnly}><RoleStartPagesPage /></ProtectedRoute></Release150Route>} />
        <Route path="/prototype/logistics/*" element={PROTOTYPES_ON ? <ProtectedRoute allow={OperationsPrincipals}><LogisticsPrototypePage /></ProtectedRoute> : <NotFoundPage />} />
        <Route path="/request-access" element={<RequestAccessPage />} />
        <Route path="/not-authorized" element={<NotAuthorizedPage />} />

        {HUBS.flatMap((hub) => hub.items.map(screenRoute)).filter(Boolean)}

        {/* A work order opened from the Operations Hub; the hub registry gives it the Operations tab strip. */}
        <Route
          path="/work-orders/:key"
          element={
            <ProtectedRoute allow={HUBS.find((h) => h.id === "operations")!.items[0]!.allow}>
              <WorkOrderDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Detail and editor pages that the areas add beyond their menu tabs. */}
        {AREA_ROUTES.map((r) => (
          <Route key={r.path} path={r.path} element={<ProtectedRoute allow={r.allow}>{r.element}</ProtectedRoute>} />
        ))}

        {/* Hub roots send a person to their own first permitted tab. */}
        {HUBS.filter((h) => h.root).map((h) => (
          <Route key={h.id} path={h.root!} element={<HubIndexRedirect path={h.root!} />} />
        ))}
        <Route path="/hub" element={<LegacyRouteRedirect to="/operations" />} />
        {/* Release 1.4.0: the owner's invite link (/admin/access?invite=upn:Role,…) keeps its query on Users & roles. */}
        <Route path="/admin/access" element={<LegacyRouteRedirect to="/admin/users-roles" />} />
        {/* Release 1.9.0 W2 (D-O160): the Purchasing view is the Watch tab of the one Purchasing workspace. */}
        <Route path="/shop-floor/purchasing" element={<LegacyPurchasingRedirect />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </>
  );
}







